<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ClientLabels from 'dashboard/components-next/Clients/ClientLabels/ClientLabels.vue';
import ClientsForm from 'dashboard/components-next/Clients/ClientsForm/ClientsForm.vue';
import ConfirmClientDeleteDialog from 'dashboard/components-next/Clients/ClientsForm/ConfirmClientDeleteDialog.vue';

const props = defineProps({
  selectedClient: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['goToClientsList']);

const { t } = useI18n();
const store = useStore();

const confirmDeleteClientDialogRef = ref(null);

const avatarFile = ref(null);
const avatarUrl = ref('');

const clientsFormRef = ref(null);

const uiFlags = useMapGetter('clients/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);

const isFormInvalid = computed(() => clientsFormRef.value?.isFormInvalid);

const clientData = ref({});

const getInitialClientData = () => {
  if (!props.selectedClient) return {};
  return { ...props.selectedClient };
};

onMounted(() => {
  Object.assign(clientData.value, getInitialClientData());
});

const createdAt = computed(() => {
  return clientData.value?.createdAt
    ? dynamicTime(clientData.value.createdAt)
    : '';
});

const lastActivityAt = computed(() => {
  return clientData.value?.lastActivityAt
    ? dynamicTime(clientData.value.lastActivityAt)
    : '';
});

const avatarSrc = computed(() => {
  return avatarUrl.value ? avatarUrl.value : clientData.value?.thumbnail;
});

const handleFormUpdate = updatedData => {
  Object.assign(clientData.value, updatedData);
};

const updateClient = async () => {
  try {
    const { customAttributes, ...basicClientData } = clientData.value;
    await store.dispatch('clients/update', basicClientData);
    await store.dispatch(
      'clients/fetchClientableInbox',
      props.selectedClient.id
    );
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.ERROR_MESSAGE'));
  }
};

const openConfirmDeleteClientDialog = () => {
  confirmDeleteClientDialogRef.value?.dialogRef.open();
};

const handleAvatarUpload = async ({ file, url }) => {
  avatarFile.value = file;
  avatarUrl.value = url;

  try {
    await store.dispatch('clients/update', {
      ...clientsFormRef.value?.state,
      avatar: file,
      isFormData: true,
    });
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.ERROR_MESSAGE'));
  }
};

const handleAvatarDelete = async () => {
  try {
    if (props.selectedClient && props.selectedClient.id) {
      await store.dispatch('clients/deleteAvatar', props.selectedClient.id);
      useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.SUCCESS_MESSAGE'));
    }
    avatarFile.value = null;
    avatarUrl.value = '';
    clientData.value.thumbnail = null;
  } catch (error) {
    useAlert(
      error.message
        ? error.message
        : t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <div class="flex flex-col items-start gap-8 pb-6">
    <div class="flex flex-col items-start gap-3">
      <Avatar
        :src="avatarSrc || ''"
        :name="selectedClient?.name || ''"
        :size="72"
        allow-upload
        @upload="handleAvatarUpload"
        @delete="handleAvatarDelete"
      />
      <div class="flex flex-col gap-1">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ selectedClient?.name }}
        </h3>
        <div class="flex flex-col gap-1.5">
          <span
            v-if="selectedClient?.identifier"
            class="inline-flex items-center gap-1 text-sm text-n-slate-11"
          >
            <span class="i-ph-user-gear text-n-slate-10 size-4" />
            {{ selectedClient?.identifier }}
          </span>
          <span class="inline-flex items-center gap-1 text-sm text-n-slate-11">
            <span
              v-if="selectedClient?.identifier"
              class="i-ph-activity text-n-slate-10 size-4"
            />
            {{ $t('CONTACTS_LAYOUT.DETAILS.CREATED_AT', { date: createdAt }) }}
            •
            {{
              $t('CONTACTS_LAYOUT.DETAILS.LAST_ACTIVITY', {
                date: lastActivityAt,
              })
            }}
          </span>
        </div>
      </div>
      <ClientLabels :client-id="selectedClient?.id" />
    </div>
    <div class="flex flex-col items-start gap-6">
      <ClientsForm
        ref="clientsFormRef"
        :client-data="clientData"
        is-details-view
        @update="handleFormUpdate"
      />
      <Button
        :label="t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')"
        size="sm"
        :is-loading="isUpdating"
        :disabled="isUpdating || isFormInvalid"
        @click="updateClient"
      />
    </div>
    <div
      class="flex flex-col items-start w-full gap-4 pt-6 border-t border-n-strong"
    >
      <div class="flex flex-col gap-2">
        <h6 class="text-base font-medium text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT') }}
        </h6>
        <span class="text-sm text-n-slate-11">
          {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT_DESCRIPTION') }}
        </span>
      </div>
      <Button
        :label="t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT')"
        color="ruby"
        @click="openConfirmDeleteClientDialog"
      />
    </div>
    <ConfirmClientDeleteDialog
      ref="confirmDeleteClientDialogRef"
      :selected-client="selectedClient"
      @go-to-clients-list="emit('goToClientsList')"
    />
  </div>
</template>
